import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, ExternalLink, Pencil, ShieldOff, ShieldCheck, ChevronRight, UserPlus, PenLine } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Table, StatusBadge, StatCard } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUB_BADGE: Record<string, string> = {
  published: "text-green-400", imported_pending_review: "text-yellow-400",
  needs_changes: "text-amber-400", rejected: "text-red-400",
};

const AuthorDetail = ({ id, onClose }: { id: string | null; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, refetch } = useQuery({
    queryKey: ["admin-author-detail", id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/admin/author-applications/${id}/detail`)).data,
  });
  const [note, setNote] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState<any>({});
  const [busy, setBusy] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);

  const a = data?.application;
  useEffect(() => {
    if (a) {
      setProfile({ display_name: a.display_name || "", handle: a.handle || "", bio: a.bio || "",
        avatar_url: a.avatar_url || "", x: a.socials?.x || "", website: a.socials?.website || "" });
      setRejecting(false);
      setNote("");
      setEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id]);

  if (!id) return null;

  const done = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["admin-author-apps"] });
  };

  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); done(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const approve = () => act(() => api.patch(`/admin/author-applications/${id}`, { action: "approve" }),
    "Author approved — notification email sent");
  const reject = () => {
    if (note.trim().length < 5) { toast.error("Write a reason the author will see (5+ chars)"); return; }
    return act(() => api.patch(`/admin/author-applications/${id}`, { action: "reject", note: note.trim() }),
      "Application rejected — the author was notified");
  };
  const setStatus = (action: "suspend" | "reinstate") =>
    act(() => api.post(`/admin/author-applications/${id}/status`, { action }), `Author ${action}d`);
  const saveProfile = () =>
    act(() => api.patch(`/admin/author-applications/${id}/profile`, {
      display_name: profile.display_name, handle: profile.handle, bio: profile.bio,
      avatar_url: profile.avatar_url, socials: { x: profile.x, website: profile.website },
    }), "Profile updated").then(() => setEditing(false));

  const uploadEditAvatar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (avatarFileRef.current) avatarFileRef.current.value = "";
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/authors/avatar", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((prev: any) => ({ ...prev, avatar_url: up.avatar_url }));
      toast.success("Photo uploaded — save the profile to apply it");
    } catch (err) { toast.error(apiError(err)); }
    setAvatarUploading(false);
  };

  const initials = (a?.display_name || "?").split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase();
  const e = data?.earnings;

  return (
    <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto bg-[#0d0e14] border-white/10"
        data-testid="author-detail-dialog">
        {!data ? (
          <>
            <DialogTitle className="sr-only">Author detail</DialogTitle>
            <p className="text-gray-500 py-10 text-center">Loading author…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start gap-4">
                {a.avatar_url ? (
                  <img src={a.avatar_url} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center shrink-0">
                    <span className="font-heading font-black text-white text-lg">{initials}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <DialogTitle className="text-white font-heading text-xl" data-testid="author-detail-name">
                    {a.display_name}
                  </DialogTitle>
                  <p className="font-mono text-xs text-[#00E5FF] mt-0.5">{a.handle ? `@${a.handle}` : "no handle yet"}</p>
                  <p className="font-mono text-[11px] text-gray-500 mt-0.5">{a.email}</p>
                </div>
                <div className="ml-auto flex flex-col items-end gap-1.5">
                  <StatusBadge status={a.status} />
                  <StatusBadge status={a.paid ? "paid" : "unpaid"} />
                </div>
              </div>
            </DialogHeader>

            {/* actions */}
            <div className="flex flex-wrap items-center gap-2 border-y border-white/[0.07] py-3">
              {a.status === "pending_review" && (
                <>
                  <Button size="sm" disabled={busy} onClick={approve} data-testid="author-detail-approve"
                    className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" disabled={busy} onClick={() => setRejecting(!rejecting)} data-testid="author-detail-reject"
                    className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
                    <X className="h-3.5 w-3.5 mr-1" /> Reject…
                  </Button>
                </>
              )}
              {a.status === "approved" && user?.role === "superadmin" && (
                <Button size="sm" data-testid="author-detail-write-article"
                  onClick={() => { onClose(); navigate(`/admin/write?author=${id}`); }}
                  className="bg-[#7C3AED]/15 text-[#c4a6ff] border border-[#7C3AED]/40 hover:bg-[#7C3AED]/25 h-8">
                  <PenLine className="h-3.5 w-3.5 mr-1" /> Write an article
                </Button>
              )}
              {a.status === "approved" && (
                <Button size="sm" disabled={busy} onClick={() => setStatus("suspend")} data-testid="author-detail-suspend"
                  className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
                  <ShieldOff className="h-3.5 w-3.5 mr-1" /> Suspend authorship
                </Button>
              )}
              {a.status === "suspended" && (
                <Button size="sm" disabled={busy} onClick={() => setStatus("reinstate")} data-testid="author-detail-reinstate"
                  className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Reinstate
                </Button>
              )}
              {a.handle && (
                <Link to={`/author/${a.handle}`} target="_blank" data-testid="author-detail-public-page"
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#00E5FF] hover:text-white transition-colors ml-auto">
                  Public byline page <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
            {rejecting && (
              <div className="space-y-2">
                <Textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={2}
                  placeholder="Reason for rejection — the author receives this by email (required)"
                  data-testid="author-reject-note" className="bg-[#14151d] border-white/10 text-white" />
                <Button size="sm" disabled={busy} onClick={reject} data-testid="author-reject-confirm"
                  className="bg-red-500 text-white hover:bg-red-600 h-8">Confirm rejection</Button>
              </div>
            )}

            {/* profile */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Profile</p>
                <button onClick={() => setEditing(!editing)} data-testid="author-profile-edit"
                  className="inline-flex items-center gap-1 font-mono text-[10px] text-gray-400 hover:text-white transition-colors">
                  <Pencil className="h-3 w-3" /> {editing ? "Cancel" : "Edit"}
                </button>
              </div>
              {editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <Input value={profile.display_name} placeholder="Display name" data-testid="author-edit-name"
                    onChange={(ev) => setProfile({ ...profile, display_name: ev.target.value })}
                    className="bg-[#14151d] border-white/10 text-white" />
                  <Input value={profile.handle} placeholder="handle (a–z, 0–9, _)" data-testid="author-edit-handle"
                    onChange={(ev) => setProfile({ ...profile, handle: ev.target.value })}
                    className="bg-[#14151d] border-white/10 text-white font-mono" />
                  <Textarea value={profile.bio} placeholder="Bio" rows={2} data-testid="author-edit-bio"
                    onChange={(ev) => setProfile({ ...profile, bio: ev.target.value })}
                    className="bg-[#14151d] border-white/10 text-white sm:col-span-2" />
                  <div className="sm:col-span-2 flex items-center gap-3" data-testid="author-edit-avatar-row">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" data-testid="author-edit-avatar-preview"
                        className="h-11 w-11 rounded-full object-cover border border-white/15" />
                    ) : (
                      <div className="h-11 w-11 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center">
                        <UserPlus className="h-4 w-4 text-[#A78BFA]" />
                      </div>
                    )}
                    <input ref={avatarFileRef} type="file" accept="image/*" className="hidden"
                      data-testid="author-edit-avatar-file" onChange={uploadEditAvatar} />
                    <Button type="button" size="sm" variant="outline" disabled={avatarUploading}
                      onClick={() => avatarFileRef.current?.click()} data-testid="author-edit-avatar-upload"
                      className="border-white/20 bg-transparent text-white hover:bg-white/5 h-8">
                      {avatarUploading ? "Uploading…" : profile.avatar_url ? "Change photo" : "Upload photo"}
                    </Button>
                    {profile.avatar_url && (
                      <button type="button" data-testid="author-edit-avatar-remove"
                        onClick={() => setProfile({ ...profile, avatar_url: "" })}
                        className="font-mono text-[10px] text-gray-500 hover:text-red-400 transition-colors">
                        Remove
                      </button>
                    )}
                    <span className="text-[10px] text-gray-500">Uploaded from device only · JPG/PNG/WebP up to 3MB</span>
                  </div>
                  <Input value={profile.x} placeholder="X / Twitter URL" data-testid="author-edit-x"
                    onChange={(ev) => setProfile({ ...profile, x: ev.target.value })}
                    className="bg-[#14151d] border-white/10 text-white" />
                  <Input value={profile.website} placeholder="Website URL" data-testid="author-edit-website"
                    onChange={(ev) => setProfile({ ...profile, website: ev.target.value })}
                    className="bg-[#14151d] border-white/10 text-white" />
                  <div>
                    <Button size="sm" disabled={busy} onClick={saveProfile} data-testid="author-profile-save"
                      className="bg-[#7C3AED] text-white hover:bg-[#6a2dd0] h-8">Save profile</Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-300 space-y-1.5">
                  <p className="text-gray-400">{a.bio || "No bio."}</p>
                  {(a.expertise || []).length > 0 && (
                    <p className="font-mono text-[10px] text-gray-500">Expertise: {(a.expertise || []).join(", ")}</p>
                  )}
                  {(a.socials?.x || a.socials?.website) && (
                    <p className="font-mono text-[10px] text-gray-500">
                      {a.socials?.x && <span className="mr-3">X: {a.socials.x}</span>}
                      {a.socials?.website && <span>Web: {a.socials.website}</span>}
                    </p>
                  )}
                  {a.note && a.status === "rejected" && (
                    <p className="font-mono text-[10px] text-red-400">Rejection note: {a.note}</p>
                  )}
                </div>
              )}
            </div>

            {/* earnings */}
            {e && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="author-earnings-summary">
                <StatCard label="Published" value={e.published} />
                <StatCard label="Total views" value={e.total_views} accent="#7C3AED" />
                <StatCard label="Accrued" value={`$${e.accrued.toFixed(2)}`} accent="#FFD600" />
                <StatCard label="Balance" value={`$${e.balance.toFixed(2)}`} accent="#10B981"
                  sub={e.pending_request ? `$${e.pending_request.toFixed(2)} payout pending` : undefined} />
              </div>
            )}

            {/* submissions */}
            <div data-testid="author-submissions">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                Submissions ({(data.submissions || []).length})
              </p>
              {(data.submissions || []).length === 0 ? (
                <p className="text-gray-600 text-sm">No stories submitted yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.submissions.map((s: any) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 bg-[#121218] border border-white/[0.07] rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        {s.status === "published" ? (
                          <Link to={`/article/${s.slug}`} target="_blank"
                            className="text-white text-sm font-medium hover:text-[#00E5FF] transition-colors line-clamp-1">
                            {s.title}
                          </Link>
                        ) : <p className="text-white text-sm font-medium line-clamp-1">{s.title}</p>}
                        <p className="font-mono text-[9px] text-gray-500 mt-0.5">
                          {timeAgo(s.created_at)} · {(s.categories || []).join(", ")}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-mono text-[10px] uppercase ${SUB_BADGE[s.status] || "text-gray-400"}`}>
                          {s.status === "imported_pending_review" ? "in review"
                            : s.status === "needs_changes" ? "needs changes" : s.status}
                        </p>
                        <p className="font-mono text-[9px] text-gray-600">{s.views || 0} views</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* audit trail */}
            <div data-testid="author-audit-trail">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Audit trail</p>
              <div className="space-y-0 border-l border-white/10 ml-1">
                {(data.trail || []).map((t: any, i: number) => (
                  <div key={i} className="relative pl-4 pb-3">
                    <span className="absolute -left-[3px] top-1.5 h-1.5 w-1.5 rounded-full bg-[#7C3AED]" />
                    <p className="text-sm text-gray-200">{t.event}</p>
                    <p className="font-mono text-[9.5px] text-gray-500 mt-0.5">
                      {timeAgo(t.at)} · {t.actor}{t.note ? ` · “${t.note}”` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PendingStoryCard = ({ s }: { s: any }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"changes" | "reject" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (action: string) => {
    if ((action === "reject" || action === "needs_changes") && note.trim().length < 5) {
      toast.error("Write a note the author will see (5+ chars)"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/author-submissions/${s.queue_id}`, { action, note: note.trim() || undefined });
      toast.success(action === "approve" ? "Story approved & published — the author was emailed"
        : action === "needs_changes" ? "Sent back for changes — the author was emailed"
        : "Story rejected — the author was emailed");
      qc.invalidateQueries({ queryKey: ["admin-author-pending"] });
      qc.invalidateQueries({ queryKey: ["admin-author-apps"] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-[#121212] border border-yellow-500/25 rounded-xl p-4" data-testid={`pending-story-${s.id}`}>
      <div className="flex gap-4">
        {s.hero_image_url && (
          <img src={s.hero_image_url} alt="" className="hidden sm:block h-20 w-32 rounded-lg object-cover border border-white/10 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-semibold line-clamp-1">{s.title}</p>
            {(s.revision_count || 0) > 0 && (
              <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
                resubmission
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-1 line-clamp-2">{s.summary}</p>
          <p className="font-mono text-[10px] text-gray-500 mt-1.5">
            <span className="text-[#00E5FF]">{s.author?.display_name}{s.author?.handle ? ` (@${s.author.handle})` : ""}</span>
            {" · "}{s.word_count || "?"} words · ~{s.read_minutes || 1} min · submitted {timeAgo(s.created_at)}
            {(s.categories || []).length > 0 && ` · ${s.categories.join(", ")}`}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button size="sm" disabled={busy || !s.queue_id} onClick={() => act("approve")}
            data-testid={`pending-approve-${s.id}`}
            className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
            <Check className="h-3.5 w-3.5 mr-1" /> Approve
          </Button>
          <Button size="sm" disabled={busy || !s.queue_id}
            onClick={() => { setMode(mode === "changes" ? null : "changes"); setNote(""); }}
            data-testid={`pending-changes-${s.id}`}
            className="bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 h-8">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Changes…
          </Button>
          <Button size="sm" disabled={busy || !s.queue_id}
            onClick={() => { setMode(mode === "reject" ? null : "reject"); setNote(""); }}
            data-testid={`pending-reject-${s.id}`}
            className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
            <X className="h-3.5 w-3.5 mr-1" /> Reject…
          </Button>
        </div>
      </div>
      {mode && (
        <div className="mt-3 flex gap-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder={mode === "changes"
              ? "What should the author change? They receive this by email (required)"
              : "Reason for rejection — the author receives this by email (required)"}
            data-testid={`pending-note-${s.id}`} className="bg-[#14151d] border-white/10 text-white flex-1" />
          <Button size="sm" disabled={busy} onClick={() => act(mode === "changes" ? "needs_changes" : "reject")}
            data-testid={`pending-note-confirm-${s.id}`}
            className={`h-auto ${mode === "changes" ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-red-500 text-white hover:bg-red-600"}`}>
            {mode === "changes" ? "Send back" : "Confirm reject"}
          </Button>
        </div>
      )}
    </div>
  );
};

const PendingStories = () => {
  const { data } = useQuery({
    queryKey: ["admin-author-pending"],
    queryFn: async () => (await api.get("/admin/author-submissions")).data,
    refetchInterval: 30000,
  });
  const subs = data?.submissions || [];
  return (
    <div className="mb-8" data-testid="authors-pending-stories">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-heading font-bold text-white text-lg">Pending stories</h2>
        <span data-testid="pending-stories-count"
          className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded-full ${
            subs.length ? "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30" : "bg-white/[0.06] text-gray-500"}`}>
          {subs.length}
        </span>
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600 ml-1">
          author submissions awaiting approval
        </p>
      </div>
      {subs.length === 0 ? (
        <p className="text-gray-600 text-sm py-4 px-4 border border-dashed border-white/10 rounded-lg"
          data-testid="pending-stories-empty">
          No author stories waiting for review — new submissions appear here instantly.
        </p>
      ) : (
        <div className="space-y-3">
          {subs.map((s: any) => <PendingStoryCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
};

const CreateAuthorDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const blank = { display_name: "", handle: "", full_name: "", email: "", password: "",
    bio: "", expertise: "", x: "", website: "", avatar_url: "" };
  const [f, setF] = useState<any>(blank);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [created, setCreated] = useState<any>(null);

  const set = (k: string) => (ev: any) => setF({ ...f, [k]: ev.target.value });

  const uploadAvatar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/authors/avatar", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev: any) => ({ ...prev, avatar_url: data.avatar_url }));
      toast.success("Avatar uploaded");
    } catch (e) { toast.error(apiError(e)); }
    setUploading(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/authors/create", {
        display_name: f.display_name, handle: f.handle || undefined,
        full_name: f.full_name || undefined, email: f.email,
        password: f.password || undefined, bio: f.bio,
        expertise: f.expertise.split(",").map((s: string) => s.trim()).filter(Boolean),
        socials: { x: f.x, website: f.website }, avatar_url: f.avatar_url || undefined,
      });
      setCreated(data);
      queryClient.invalidateQueries({ queryKey: ["admin-author-apps"] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const close = () => { setF(blank); setCreated(null); onClose(); };
  const field = "bg-[#14151d] border-white/10 text-white";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto bg-[#0d0e14] border-white/10"
        data-testid="create-author-dialog">
        {created ? (
          <div data-testid="ca-success">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Author created 🎉</DialogTitle>
            </DialogHeader>
            <p className="text-gray-400 text-sm mt-2">
              <span className="text-white font-medium">@{created.handle}</span> is live, approved and can sign in
              right away. <span className="text-amber-400">No email was sent</span> — share these credentials yourself.
              They'll be asked to set a new password at first sign-in.
            </p>
            <div className="mt-4 rounded-lg border border-white/10 bg-black/40 p-4 font-mono text-sm space-y-2">
              <p className="text-gray-300">Email: <span className="text-white">{created.email}</span></p>
              <p className="text-gray-300">Temp password: <span className="text-[#00E5FF]" data-testid="ca-temp-password">{created.temp_password}</span></p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" data-testid="ca-copy-credentials"
                onClick={() => { navigator.clipboard.writeText(`Email: ${created.email}\nPassword: ${created.temp_password}\nSign in: ${window.location.origin}/login`); toast.success("Credentials copied"); }}
                className="bg-[#7C3AED] text-white hover:bg-[#6a2dd0] h-8">Copy credentials</Button>
              <Link to={`/author/${created.handle}`} target="_blank"
                className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#00E5FF] hover:text-white transition-colors">
                Public byline page <ExternalLink className="h-3 w-3" />
              </Link>
              <Button size="sm" variant="outline" onClick={close} data-testid="ca-done"
                className="ml-auto border-white/20 bg-transparent text-white hover:bg-white/5 h-8">Done</Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Create author account</DialogTitle>
            </DialogHeader>
            <p className="text-gray-500 text-xs -mt-1">
              Instantly approved with full Studio access — no emails are sent to the author.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
              <Input value={f.display_name} onChange={set("display_name")} placeholder="Display name (byline) *"
                data-testid="ca-display-name" className={field} />
              <Input value={f.handle} onChange={set("handle")} placeholder="handle (blank = auto)"
                data-testid="ca-handle" className={`${field} font-mono`} />
              <Input value={f.full_name} onChange={set("full_name")} placeholder="Full name (account, optional)"
                data-testid="ca-full-name" className={field} />
              <Input value={f.email} onChange={set("email")} placeholder="Email *" type="email"
                data-testid="ca-email" className={field} />
              <Input value={f.password} onChange={set("password")} placeholder="Password (blank = auto-generate)"
                data-testid="ca-password" className={`${field} sm:col-span-2 font-mono`} />
              <Textarea value={f.bio} onChange={set("bio")} placeholder="Bio" rows={3}
                data-testid="ca-bio" className={`${field} sm:col-span-2`} />
              <Input value={f.expertise} onChange={set("expertise")} placeholder="Expertise (comma separated: DeFi, Markets…)"
                data-testid="ca-expertise" className={`${field} sm:col-span-2`} />
              <Input value={f.x} onChange={set("x")} placeholder="X / Twitter URL" data-testid="ca-x" className={field} />
              <Input value={f.website} onChange={set("website")} placeholder="Website URL" data-testid="ca-website" className={field} />
              <div className="sm:col-span-2 flex items-center gap-3">
                {f.avatar_url ? (
                  <img src={f.avatar_url} alt="" data-testid="ca-avatar-preview"
                    className="h-11 w-11 rounded-full object-cover border border-white/15" />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-[#A78BFA]" />
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  data-testid="ca-avatar-file" onChange={uploadAvatar} />
                <Button type="button" size="sm" variant="outline" disabled={uploading}
                  onClick={() => fileRef.current?.click()} data-testid="ca-avatar-upload"
                  className="border-white/20 bg-transparent text-white hover:bg-white/5 h-8">
                  {uploading ? "Uploading…" : f.avatar_url ? "Change photo" : "Upload photo"}
                </Button>
                <span className="text-[10px] text-gray-500">Optional · JPG/PNG/WebP up to 3MB</span>
              </div>
            </div>
            <div className="mt-2">
              <Button disabled={busy || !f.display_name.trim() || !f.email.trim()} onClick={submit}
                data-testid="ca-submit" className="bg-[#7C3AED] text-white hover:bg-[#6a2dd0]">
                {busy ? "Creating…" : "Create author"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Authors() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("pending_review");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: apps } = useQuery({
    queryKey: ["admin-author-apps", status],
    queryFn: async () => (await api.get(`/admin/author-applications?status=${status}`)).data,
  });

  const approve = async (app: any) => {
    try {
      await api.patch(`/admin/author-applications/${app.id}`, { action: "approve" });
      toast.success(`Approved — ${app.email} was notified by email`);
      queryClient.invalidateQueries({ queryKey: ["admin-author-apps"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-authors-page">
      <PageHeader title="Author Program" sub="Review applications, manage author profiles and audit their history"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreating(true)} data-testid="create-author-button"
              className="bg-[#7C3AED] text-white hover:bg-[#6a2dd0]">
              <UserPlus className="h-4 w-4 mr-1.5" /> Create author
            </Button>
            <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 bg-[#1A1A1A] border-white/10 text-white" data-testid="authors-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft_unpaid">Unpaid drafts</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          </div>
        } />

      <PendingStories />

      <Table headers={["Author", "Bio", "Expertise", "Paid", "Applied", "Status", "Actions"]}>
        {(apps || []).map((a: any) => (
          <tr key={a.id} data-testid={`author-app-row-${a.id}`}
            onClick={() => setDetailId(a.id)}
            className="cursor-pointer hover:bg-white/[0.03] transition-colors">
            <td className="px-4 py-3">
              <p className="text-white font-medium flex items-center gap-1.5">
                {a.display_name}
                <ChevronRight className="h-3 w-3 text-gray-600" />
              </p>
              <p className="font-mono text-[10px] text-gray-500">
                {a.handle ? `@${a.handle} · ` : ""}{a.email}
              </p>
            </td>
            <td className="px-4 py-3 text-sm text-gray-400 max-w-[240px]">
              <p className="line-clamp-2">{a.bio || "—"}</p>
            </td>
            <td className="px-4 py-3 font-mono text-[10px] text-gray-400">
              {(a.expertise || []).join(", ") || "—"}
            </td>
            <td className="px-4 py-3"><StatusBadge status={a.paid ? "paid" : "unpaid"} /></td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400">{timeAgo(a.created_at)}</td>
            <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
            <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}>
              {a.status === "pending_review" ? (
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => approve(a)} data-testid={`author-approve-${a.id}`}
                    className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" onClick={() => setDetailId(a.id)} data-testid={`author-reject-${a.id}`}
                    className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
                    <X className="h-3.5 w-3.5 mr-1" /> Reject…
                  </Button>
                </div>
              ) : (
                <button onClick={() => setDetailId(a.id)} data-testid={`author-open-${a.id}`}
                  className="font-mono text-[10px] text-gray-500 hover:text-white transition-colors">
                  view profile →
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {(apps || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-6 text-center py-8 border border-dashed border-white/10 rounded-lg">
          No applications with this status.
        </p>
      )}

      <AuthorDetail id={detailId} onClose={() => setDetailId(null)} />
      <CreateAuthorDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
